import brandView from "app/analytics/mutations/brandView"
import { BrandDetails } from "app/brands/components/BrandDetails"
import { NortherPageProps } from "app/common/components/NortherPageProps"
import { decodeWithTE } from "app/common/core/decode"
import { logException } from "app/common/logging"
import { bindCommon } from "app/common/next"
import { UrlMgr } from "app/common/routes"
import { ldJsonArticle } from "app/common/seo/jdJson"
import { createSeoMetadata } from "app/common/seo/seoEssentials"
import {
  getEmptyStaticPathsResult,
  getStaticPropsResult,
  notFoundOrThrow,
} from "app/common/ui/next"
import { parseEntityIds, StructuredTextEntities } from "app/common/utilities/structuredText"
import { toTitleString } from "app/common/web"
import { getBrandBySlug, getBrandsByIds } from "app/data/brands/brand.queries"
import BlogLayout from "app/layouts/BlogLayout"
import { BrandVM } from "app/models"
import { BlitzPage, GetStaticPaths, GetStaticProps, useMutation } from "blitz"
import { LocaleD } from "db"
import { array, option, task, taskEither } from "fp-ts"
import { pipe } from "fp-ts/function"
import * as t from "io-ts"
import { useEffect } from "react"

// type BrandPath = StaticPath<{
//   slug: string
// }>

// const mapBrandToBrandPath = ({ slug }: Brand): BrandPath => ({
//   locale: CanadianLocales.En,
//   params: {
//     slug,
//   },
// })

// export const getStaticPaths: GetStaticPaths = () =>
//   pipe(
//     getBrandPaths(),
//     taskEither.map(array.map(mapBrandToBrandPath)),
//     taskEither.map(getStaticPathsResult),
//     taskEither.mapLeft(logException),
//     taskEither.fold(justThrow, task.of)
//   )()
// ts-prune-ignore-next
export const getStaticPaths: GetStaticPaths = () => Promise.resolve(getEmptyStaticPathsResult())

const Slug = t.string

const decodeSlug = decodeWithTE(Slug)

interface BrandPageProps extends NortherPageProps {
  brand: BrandVM
  entities: StructuredTextEntities
  locale: LocaleD
  slug: string
}

const brandVmMap = (brand: BrandVM, locale: LocaleD): ldJsonArticle => ({
  "@context": "https://schema.org",
  "@type": "Article",
  dateModified: brand.updatedAt.toISOString(),
  datePublished: brand.publishedAt.toISOString(),
  headline: brand.name,
  name: brand.name,
  publisher: {
    "@type": "Organization",
    name: "Norther.ca",
  },
  url: UrlMgr.BrandPage({
    locale,
    slug: brand.slug,
  }),
})

// ts-prune-ignore-next
export const getStaticProps: GetStaticProps<BrandPageProps> = ({ locale, params }) =>
  pipe(
    taskEither.Do,
    bindCommon(locale),
    taskEither.apS("slug", decodeSlug(params?.slug)),
    taskEither.bind("brandMap", ({ slug }) => getBrandBySlug(slug)),
    taskEither.bind("brand", ({ brandMap, locale }) => pipe(brandMap[locale], taskEither.of)),
    taskEither.bind("st", ({ brand: { content } }) => pipe(content, parseEntityIds, taskEither.of)),
    taskEither.bind("entities", ({ locale, st: { brandIds } }) =>
      pipe(
        brandIds,
        option.fromPredicate(array.isNonEmpty),
        option.traverse(taskEither.ApplicativePar)(getBrandsByIds(locale)),
        taskEither.map(option.getOrElse(() => [] as readonly BrandVM[])),
        taskEither.chain((contentBrands) => taskEither.of({ brands: contentBrands }))
      )
    ),
    taskEither.bind("seoMetadata", ({ brand, brandMap: { en_ca, fr_ca }, locale, ogLocale, t }) =>
      pipe(
        createSeoMetadata({
          canonicalUrl: UrlMgr.BrandPage({
            locale,
            slug: brand.slug,
          }),
          ldJsonArticle: brandVmMap(brand, locale),
          openGraphMetadata: {
            description: `Discover the brand ${brand.name} on Norther and support them if you like what you see`,
            locale: ogLocale,
            siteName: "Norther",
            title: toTitleString([
              brand.name,
              t("canadian-brands"),
              t("buy-local-buy-canadian"),
              `Norther`,
            ]),
            type: option.none,
            url: UrlMgr.BrandPage({
              locale,
              slug: en_ca.slug,
            }),
          },
          title: toTitleString([
            brand.name,
            t("canadian-brands"),
            t("buy-local-buy-canadian"),
            `Norther`,
          ]),
          urlMap: {
            en_ca: UrlMgr.BrandPage({
              slug: en_ca.slug,
            }),
            fr_ca: UrlMgr.BrandPage({
              locale: LocaleD.fr_ca,
              slug: fr_ca.slug,
            }),
            "x-default": UrlMgr.BrandPage({
              slug: en_ca.slug,
            }),
          },
        }),
        taskEither.of
      )
    ),
    taskEither.map(({ brand, entities, locale, seoMetadata, slug }) => ({
      brand,
      entities,
      locale,
      seoMetadata,
      slug,
    })),
    taskEither.bimap(logException, getStaticPropsResult),
    taskEither.fold(notFoundOrThrow, task.of)
  )()

const BrandPage: BlitzPage<BrandPageProps> = ({ brand, entities, locale }) => {
  const [createBrandView] = useMutation(brandView)

  useEffect(() => {
    createBrandView({ id: brand.id, locale })
  }, [createBrandView, brand.id, locale])

  return <BrandDetails brand={brand} entities={entities} locale={locale} />
}

BrandPage.suppressFirstRenderFlicker = true

BrandPage.getLayout = BlogLayout.getLayout

// ts-prune-ignore-next
export default BrandPage
